import React, { useEffect, useState, Fragment } from 'react';
import { Stream } from '../../Models/Stream';
import {
  Typography,
  Grid,
  CircularProgress,
  Box,
  Stack,
  Button
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useNavigate } from "react-router-dom";
import { ArrowRight } from '@mui/icons-material';
import VideoCarousel from './VideoCarousel';
import VideoGrid from './VideoGrid';
import './VideoListingStyles.css';
import { useAPIContext } from '../../Context/APIContext';
import { VideoListingPageType } from '../../Models/VideoListingPageType';
import { Category } from '../../Models/Category';

const useStyles = makeStyles({
  viewAllButton: {
    height: '1rem',
  },
  card: {
    transition: 'transform 0.3s ease, box-shadow 0.3s ease !important',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    maxWidth: '300px',
    width: '100%',
    '&:hover': {
      transform: 'scale(1.05)',
      boxShadow: '0 6px 12px rgba(0, 0, 0, 0.3)',
    },
  },
  carouselItem: {
    padding: "1em",
    maxWidth: "300px",
    width: "100%",
  },
  categoryDescription: {
    width: '100%',
    '@media (min-width: 960px)': {
      width: '80%',
    },
  },
});


interface VideoListingProps {
  type: VideoListingPageType;
  categoryIds: number[];
}

interface VideoCategories {
  category: Category;
  streams: Stream[];
}

export default function VideoListing({type, categoryIds}: VideoListingProps) {
  const classes = useStyles();
  const [videoCategories, setVideoCategories] = useState<VideoCategories[]>([]);
  const [savedStreams, setSavedStreams] = useState<Stream[]>([]);
  const [filteredStreams, setFilteredStreams] = useState<Stream[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  const [searchTerm] = useState<string>("");
  const { apiFetch } = useAPIContext();
  const navigate = useNavigate();
  const pageTitle = type === VideoListingPageType.Staff ? "Staff Zone" : type === VideoListingPageType.Students ? "Student Zone" : "";

  useEffect(() => {
    const fetchStreamData = async () => {
      setLoading(true);

      var vc: VideoCategories[] = [];
      for (const categoryId of categoryIds) {
        const category = await apiFetch<Category>(`Categories/${categoryId}`, 'GET', Category.fromJson);
        var streams = await apiFetch<Stream[]>(`Streams?categoryId=${categoryId}&limit=15`, 'GET', Stream.fromJsonArray);

        vc = [...vc, { category: category!, streams: streams || [] }];
      }
      setVideoCategories(vc);
      var savedStreams = await apiFetch<Stream[]>('Users/SavedStreams', 'GET', Stream.fromJsonArray)
      setSavedStreams(savedStreams || []);

      setLoading(false);
    };

    fetchStreamData();
  }, [apiFetch, categoryIds, type]);

  useEffect(() => {
    const searchStreamData = async () => {
      setSearchLoading(true);
      const filteredData = await apiFetch<Stream[]>(`Streams?limit=${1000}&searchText=${searchTerm.toLowerCase()}`,'GET',Stream.fromJsonArray)
      if (filteredData) setFilteredStreams(filteredData);
      setSearchLoading(false);
    };
    searchStreamData();
  }, [searchTerm, apiFetch]);

  const ViewAll = (categoryId: number) => navigate(`/videos/${categoryId}`);

  const renderCategory = (
    streams: Stream[],
    category: Category
  ) => (
    <Box className="header-section" mt={2} sx={{ mb: { xs: 3, md: 2 } }}>
      <Typography variant="h6" className="header-text">{category.name}</Typography>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md>
          <Typography
            variant="body2"
            color="textSecondary"
            className={classes.categoryDescription}
          >
            {category.description}
          </Typography>
        </Grid>
        <Grid item>
          <Button variant="text" color='primary' onClick={() => ViewAll(category.id)} endIcon={<ArrowRight />}>
            <b>View All</b>
          </Button>
        </Grid>
      </Grid>
      <VideoCarousel streams={streams} savedStreams={savedStreams} />
    </Box>
  );

  return (
    <Stack
      spacing={2}
      sx={{
        alignItems: 'center',
        mx: { xs: 3, sm: 3, md: 3 },
        pb: 5,
        mt: { xs: 1, md: 0 },
      }}
    >
      <Box sx={{ width: '100%', maxWidth: '1700px', pt: 1 }}>
        {/* Header */}
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          mb={2}
          wrap="wrap"
        >
          <Grid item sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              className="header-text"
              sx={{
                fontSize: '1.5em',
              }}
            >{pageTitle}</Typography>
          </Grid>
        </Grid>


        {/* Conditional Content */}
        {searchTerm === "" ? (
          loading ? (
            <Box textAlign="center">
              <CircularProgress style={{ color: '#3D9BE9' }} />
            </Box>
          ) : (
            <>
              {videoCategories.map(({ category, streams }) => (
                <Fragment key={category.id}>
                  {renderCategory(streams, category)}
                </Fragment>
              ))}

            </>
          )
        ) : (
          searchLoading ? (
            <Box textAlign="center">
              <CircularProgress style={{ color: '#3D9BE9' }} />
            </Box>
          ) : filteredStreams.length > 0 ? (
            <Box className="header-section">
              <VideoGrid streams={filteredStreams} savedStreams={savedStreams} />
            </Box>
          ) : (
            <Typography variant="h6" align="center" color="textSecondary" sx={{ minHeight: '300px' }}>
              No Videos found.
            </Typography>
          )
        )}
      </Box>
    </Stack>
  );
}